import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { sendContactEnquiry } from '../api';

// Shared contact band (id="rs-contact"): form, map and the two sedi, rendered
// at the bottom of every page by PageLayout.
const ContactSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#00425c',
  backgroundImage:
    'radial-gradient(circle at 15% 20%, rgba(134,163,21,0.16), transparent 42%), radial-gradient(circle at 85% 80%, rgba(193,10,39,0.12), transparent 45%), radial-gradient(circle at 75% 10%, rgba(128,172,192,0.14), transparent 38%)',
  padding: '96px 0',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '64px 0',
  }
}));

const ContactForm = styled(Paper)(({ theme }) => ({
  padding: '30px',
  borderRadius: '20px',
  boxShadow: '0 12px 36px rgba(0, 0, 0, 0.25)',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.10)',
  backdropFilter: 'blur(16px)',
  color: '#fff',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '18px',
  '& .MuiInputBase-input': {
    color: '#fff',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.55)',
    opacity: 1,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.16)',
      transition: 'border-color 0.3s ease',
    },
    '&:hover fieldset': {
      borderColor: '#8fc1d6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8fc1d6',
    },
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#00425c',
  padding: '12px 32px',
  fontSize: '15.5px',
  '&:hover': {
    backgroundColor: '#e9f1f6',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 24px rgba(2, 49, 68, 0.45)',
  }
}));

const ContactCard = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.09)',
  backdropFilter: 'blur(12px)',
  color: '#fff',
  borderRadius: '18px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '15px',
  '&:hover': {
    transform: 'translateY(-5px)',
    backgroundColor: 'rgba(255,255,255,0.08)',
  }
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  '& svg': {
    color: '#8fc1d6',
    fontSize: '1.8rem'
  }
}));

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    message: '',
    company: '' // honeypot — real users never fill this
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await sendContactEnquiry(formData);
      setFormData({ name: '', surname: '', email: '', message: '', company: '' });
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Il tuo messaggio è stato inviato — ti risponderemo al più presto.'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message || 'Qualcosa è andato storto. Scrivici a info@qasc.it.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((s) => ({ ...s, open: false }));
  };

  return (
    <>
      <ContactSection id="rs-contact">
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#8fc1d6',
              fontFamily: '"Sora", sans-serif',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            Contattaci
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center', color: '#fff', mb: 3 }}>
            Richiedici maggiori informazioni
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.78)', maxWidth: '680px', mx: 'auto', mb: 5 }}
          >
            Richiedici maggiori informazioni per personalizzare, in base alle tue esigenze, i
            nostri servizi di formazione e consulenza.
          </Typography>

          <Grid container spacing={4}>
            {/* Left side - Map */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '100%' }}>
                <Box
                  component="iframe"
                  src="https://www.google.com/maps?q=Viale%20Sanremo%2036%2C%2030038%20Spinea%20VE%2C%20Italia&output=embed"
                  width="100%"
                  height="100%"
                  sx={{ border: 0, minHeight: { xs: '280px', md: '400px' }, borderRadius: '20px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="La nostra sede - Viale Sanremo 36, Spinea (VE)"
                />
              </Box>
            </Grid>

            {/* Right side - Contact Form */}
            <Grid item xs={12} md={6}>
              <ContactForm elevation={0}>
                <form onSubmit={handleSubmit}>
                  {/* Honeypot: hidden from users, catches bots. */}
                  <Box
                    component="input"
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={formData.company}
                    onChange={handleChange}
                    sx={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        placeholder="Nome e Cognome o Ragione Sociale"
                        name="name"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        placeholder="Indirizzo e-mail"
                        name="email"
                        variant="outlined"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        placeholder="Il tuo messaggio"
                        name="message"
                        variant="outlined"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', mb: 2, lineHeight: 1.6 }}>
                        Inviando il modulo acconsenti al trattamento dei tuoi dati personali come
                        dal Regolamento (UE) 2016/679 (GDPR).
                      </Typography>
                      <SubmitButton
                        type="submit"
                        variant="contained"
                        disableElevation
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Invio in corso…' : 'Invia'}
                      </SubmitButton>
                    </Grid>
                  </Grid>
                </form>
              </ContactForm>
            </Grid>
          </Grid>

          {/* Sedi cards */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <ContactCard>
                <ContactIcon>
                  <LocationOnIcon />
                </ContactIcon>
                <Box>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 0.5 }}>
                    Sede operativa e aule formative
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.70)' }}>
                    Viale Sanremo, 36 — 30038 Spinea (VE)<br />
                    Telefono +39 041 995202
                  </Typography>
                </Box>
              </ContactCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ContactCard>
                <ContactIcon>
                  <PhoneIcon />
                </ContactIcon>
                <Box>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 0.5 }}>
                    Sede operativa
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.70)' }}>
                    Via Monte Nero, 4 — 67043 Celano (AQ)<br />
                    Telefono / Fax +39 0863 792752
                  </Typography>
                </Box>
              </ContactCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ContactCard>
                <ContactIcon>
                  <EmailIcon />
                </ContactIcon>
                <Box>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 0.5 }}>
                    Scrivici
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.70)' }}>
                    info@qasc.it<br />
                    formazione@qasc.it
                  </Typography>
                </Box>
              </ContactCard>
            </Grid>
          </Grid>
        </Container>
      </ContactSection>

      {/* Result message */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Contact;
